import { call, put } from "typed-redux-saga";
import { AppConfig } from "../../AppConfig";
import { UserExidResponse } from "../../core/api/requests/auth/exid/types";
import { LoginResponse } from "../../core/api/requests/auth/login/types";
import { UserTemporaryResponse } from "../../core/api/requests/auth/temporary/types";
import { UserTemporaryMailResponse } from "../../core/api/requests/auth/temporary_mail/types";
import { UserResponse } from "../../core/api/requests/auth/user/types";
import {
  loginApi,
  passwordChangeApi,
  passwordReminderApi,
  userApi,
  userEditApi,
  userExidApi,
  userMailChangeApi,
  userMailChangeSendApi,
  userMailCheckApi,
  userQuitApi,
  userRegisterApi,
  userRegisterMailApi,
  userTemporaryApi,
  userTemporaryMailApi,
} from "../../core/http/httpClient";
import { LocalStorageManager } from "../../core/storage/LocalStorageManager";
import { ApiResponse } from "../../core/types";
import { takeEveryFsa } from "../operations";
import { resetToInitialState } from "../resetToInitialState";
import { authActions } from "./actions";

export function* authSaga() {
  yield takeEveryFsa(authActions.login.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<LoginResponse> = yield call(() => {
      return loginApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    LocalStorageManager.Instance.saveObject(
      AppConfig.Instance.LocalStorageKey["accessToken"],
      response.data.access_token,
    );

    yield* put(
      authActions.login.done({
        params: action.payload,
        result: response.data,
      }),
    );
    onSuccess?.(response.data);

    yield* put(authActions.getUser.started({}));
  });

  yield takeEveryFsa(authActions.getUser.started, function* (action) {
    const { onComplete, onError, onSuccess } = action.payload;
    const { response, error }: ApiResponse<UserResponse> = yield call(() => {
      return userApi
        .call({})
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.getUser.done({
        params: action.payload,
        result: {
          name: response.data.name,
          phoneNumber: response.data.tel,
        },
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.saveExternalId.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<UserExidResponse> = yield call(
      () => {
        return userExidApi
          .call({ app_token: encodeURIComponent(body.app_token) })
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      },
    );

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.saveExternalId.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.logout.started, function* (action) {
    const { onComplete, onError, onSuccess } = action.payload;
    // const { response, error }: ApiResponse<{}> = yield call(() => {
    //   return logoutApi
    //     .call({})
    //     .then((response) => ({ response }))
    //     .catch((error) => ({ error }));
    // });

    onComplete?.();

    // if (!response || error) return onError?.(error);

    yield* put(
      authActions.logout.done({
        params: action.payload,
        result: {},
      }),
    );
    yield* resetToInitialState();

    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["accessToken"],
    );

    onSuccess?.({});
  });

  yield takeEveryFsa(
    authActions.sendRegistrationEmail.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;
      const { response, error }: ApiResponse<UserTemporaryResponse> =
        yield call(() => {
          return userTemporaryApi
            .call(body)
            .then((response) => ({ response }))
            .catch((error) => ({ error }));
        });

      onComplete?.();

      if (!response || error) return onError?.(error);

      yield* put(
        authActions.sendRegistrationEmail.done({
          params: action.payload,
          result: response.data,
        }),
      );

      onSuccess?.(response.data.key);
    },
  );

  yield takeEveryFsa(
    authActions.sendRegistrationEmailOnly.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;
      const { response, error }: ApiResponse<UserTemporaryMailResponse> =
        yield call(() => {
          return userTemporaryMailApi
            .call(body)
            .then((response) => ({ response }))
            .catch((error) => ({ error }));
        });

      onComplete?.();

      if (!response || error) return onError?.(error);

      yield* put(
        authActions.sendRegistrationEmail.done({
          params: action.payload,
          result: response.data,
        }),
      );

      onSuccess?.(response.data);
    },
  );

  yield takeEveryFsa(authActions.register.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return userRegisterApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.register.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.registerMail.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return userRegisterMailApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.registerMail.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(
    authActions.editUserInformation.started,
    function* (action) {
      const { onComplete, onError, onSuccess, ...body } = action.payload;
      const { response, error }: ApiResponse<{}> = yield call(() => {
        return userEditApi
          .call(body)
          .then((response) => ({ response }))
          .catch((error) => ({ error }));
      });

      onComplete?.();

      if (!response || error) return onError?.(error);

      yield* put(
        authActions.editUserInformation.done({
          params: action.payload,
          result: response.data,
        }),
      );

      onSuccess?.(response.data);
      yield* put(authActions.getUser.started({}));
    },
  );

  yield takeEveryFsa(authActions.sendChangeEmail.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return userMailChangeSendApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.sendChangeEmail.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.verifyEmailKey.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return userMailCheckApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.verifyEmailKey.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.changeEmail.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return userMailChangeApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.changeEmail.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.forgetPassword.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return passwordReminderApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.forgetPassword.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.resetPassword.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return passwordChangeApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.resetPassword.done({
        params: action.payload,
        result: response.data,
      }),
    );

    onSuccess?.(response.data);
  });

  yield takeEveryFsa(authActions.deactivate.started, function* (action) {
    const { onComplete, onError, onSuccess, ...body } = action.payload;
    const { response, error }: ApiResponse<{}> = yield call(() => {
      return userQuitApi
        .call(body)
        .then((response) => ({ response }))
        .catch((error) => ({ error }));
    });

    onComplete?.();

    if (!response || error) return onError?.(error);

    yield* put(
      authActions.deactivate.done({
        params: action.payload,
        result: response.data,
      }),
    );
    yield* resetToInitialState();

    LocalStorageManager.Instance.removeObject(
      AppConfig.Instance.LocalStorageKey["accessToken"],
    );

    onSuccess?.(response.data);
  });
}
